// Element hooks shared between the app and the Playwright suite.
//
// `e2e-testing/constants.ts` re-exports everything here, so both packages read
// the same names and a rename is a `tsc` failure on both sides instead of a
// selector that quietly matches nothing.
//
// These are added ALONGSIDE the existing `id` attributes, never instead of
// them: several of those ids are load-bearing for the app rather than the
// tests — `#sidenav-toggle` is styled by id in `landing/themes/globals.css`
// (it is what reveals the drawer below 1024px), `#agree-input` is a
// `<label htmlFor>` target, and `#root` / `#modal-root`
// are `getElementById` anchors. Dropping an id is a separate, case-by-case
// decision; adding a test hook is not.

/** Mount point for the whole React tree (`frontend/public/index.html`). */
export const appRootId = 'root'

/** Default id of the shared `Spinner`. */
export const spinnerId = 'spinner'

/** Marks a skeleton placeholder standing in for not-yet-loaded content. */
export const skeletonClass = 'page-loading'

/**
 * The element `DashboardLayout` puts every page inside.
 *
 * It styles NOTHING, and never has: no stylesheet in either package declares
 * `.dashboard-container`, so next to the layout's own padding utilities it
 * reads as a class someone forgot to write rules for. Its one job is to be an
 * anchor -- `tourSteps.ts` spotlights it for the statistics step -- and naming
 * it here is what makes that job visible from both ends. A grep for the
 * string now finds this comment instead of nothing.
 */
export const dashboardContainerClass = 'dashboard-container'

/**
 * The premium lock. It used to be selected in the e2e suite as
 * `svg[data-icon="lock"]`, which was Font Awesome's own markup and went with
 * it; the layout assertion that reads it has nothing else to aim at.
 */
export const premiumLockTestId = 'premium-lock'

export const testIds = {
  // Navigation
  navProfileButton: 'profile-nav-button',
  navSubjectButton: 'subject-nav-button',
  navDashboardButton: 'dashboard-nav-button',
  navHistoryButton: 'history-nav-button',
  // The rail's bottom row, which opens the account menu. Design 9a moved
  // Profil, Contact, the teacher/admin groups and logout behind it, so this is
  // now the one hook that is always in the rail itself -- `openSidenav` asserts
  // on it to know the drawer actually landed on screen.
  navAccountButton: 'account-nav-button',
  logoutButton: 'logout-button',
  sidenavToggle: 'sidenav-toggle',

  // Guided tour
  startSolvingGridsButton: 'tutorial-start-now-btn',
  tourCard: 'tour-card',
  // Distinct from `modalCloseIcon`: clicking this opens CloseTutorialModal
  // WITHOUT unmounting the tour, so for as long as that modal is up both X
  // buttons are in the document at once.
  tourCloseIcon: 'tour-close-icon',
  tourPrevButton: 'tour-card-prev-btn',
  tourNextButton: 'tour-card-next-btn',
  tourFinishButton: 'tour-card-finish-btn',
  closeTutorialButton: 'close-tutorial-btn',

  // page:rezolva-grile
  nextGridButton: 'next-grid-button',
  sendAnswerButton: 'send-answer-button',
  saveGridButton: 'save-grid-btn',
  saveNoteButton: 'save-note-btn',

  // Login. The auth pages each render their own email/password field, so the
  // hooks are page-scoped even though the underlying ids collide today.
  loginEmailInput: 'login-email-input',
  loginPasswordInput: 'login-password-input',
  loginButton: 'login-button',
  loginError: 'login-error',

  // Register
  registerNameInput: 'name-input',
  registerEmailInput: 'register-email-input',
  registerPasswordInput: 'register-password-input',
  registerConfirmPasswordInput: 'register-confirm-password-input',
  registerAgreeCheckbox: 'agree-input',
  registerButton: 'signup-button',
  registerUsedEmailError: 'already-used-email-error',
  registerEmailError: 'email-error',
  registerSmallPasswordError: 'register-small-password-error',
  registerMismatchError: 'register-different-password-error',

  // page:recuperare-parola
  forgotPasswordEmailInput: 'forgot-password-email-input',
  forgotPasswordButton: 'forgot-password-button',
  forgotPasswordError: 'forgot-password-error',
  forgotPasswordSuccess: 'forgot-password-success',

  // page:resetare-parola
  resetPasswordInput: 'reset-password-input',
  resetPasswordConfirmInput: 'reset-confirm-password-input',
  resetPasswordButton: 'reset-password-button',
  resetPasswordSmallPasswordError: 'reset-small-password-error',
  resetPasswordMismatchError: 'different-password-error',

  // page:confirmare-cont
  confirmAccountSuccess: 'confirm-account-success',

  // page:profil
  openChangePasswordButton: 'open-change-password-button',
  currentPasswordInput: 'current-password-input',
  newPasswordInput: 'new-password-input',
  confirmNewPasswordInput: 'confirm-new-password-input',
  changePasswordButton: 'change-password-button',
  wrongCurrentPasswordError: 'wrong-current-password-error',
  smallNewPasswordError: 'small-new-password-error',

  // page:abonamente
  voucherInput: 'voucher-input',
  applyVoucherButton: 'apply-voucher-button',

  // Modals
  modalCloseIcon: 'close-modal-icon',
  chooseUniversityModalNextButton: 'choose-university-modal-next-button',
  detailsModalPhoneInput: 'details-modal-phone-input',

  // page:istoric-grile
  gridsHistoryTab: 'grids-history-tab',
  // The grids tab is the one the page opens on, so a spec that wants a second
  // surface on this page needs a way back to the saved list.
  savedGridsTab: 'saved-grids-tab',
  subjectsHistoryTab: 'subjects-history-tab',
  historyFiltersButton: 'history-filters-button',

  // page:simulari
  // The first past-year exam in the list. Its "Rezolvă" button only exists once
  // the row is open — the row opens in place into the admission scale, and the
  // button lives in there with the materie choice it may need — so a spec that
  // wants to sit a subject clicks this first.
  pastSubjectRow: 'past-subject-row',
  startNewSubject: 'start-new-subject',
  // Design 6a moved handing the paper in out of the footer and into the
  // question navigator, which is the only place the blank-question count is
  // visible. Above `xl` the navigator is a permanent rail, so the button is
  // always on screen; below it the navigator is a dialog and a spec has to open
  // it through `openSubjectNavigator` first.
  sendSubjectAnswerButton: 'send-subject-answer-button',
  openSubjectNavigator: 'open-subject-navigator',
  // The way out of an exam in progress. It used to be a button at the foot of
  // the grid list (and, below `xl`, inside the grid-list modal, which is where
  // `changeSubject` lived) — design 6a moves it to the exam bar, so it is the
  // same control at every width and a spec no longer has to open a panel to
  // reach it.
  abandonSubject: 'abandon-subject',
  showGridList: 'show-grid-list',
  changeSubject: 'change-subject',
  // The grade on /simulare-incheiata. It used to be the one in the
  // "Mai pregătește-te" dialog; the dialog is gone and the page took both the
  // number and the hook, so `subject.spec.ts` still cross-checks the app's
  // grading against the graded response — one screen later.
  subjectResultGrade: 'subject-result-grade',
  closeSubjectResult: 'close-subject-result',
  // One sitting in the /simulari attempts panel, which opens its results.
  subjectAttemptLink: 'subject-attempt-link',
  // One simulation in /istoric-grile, which now navigates instead of expanding.
  historySubjectRow: 'history-subject-row',

  // Toasts
  closeCommunityToast: 'close-community-toast',

  // What `components/ErrorBoundary` shows when a render throws. A crashed
  // route used to leave an empty #root, which is what the route checks looked
  // for; now it leaves this, so they look for this.
  errorScreen: 'error-screen',
} as const

// Hooks whose identity depends on runtime data. Both packages build them from
// the same helper so a spec can assert against the row it actually set up
// rather than a positional index.

/** `letter` is A–F, matching the badge the student sees. */
export const choiceTestId = (letter: string) => `choice-${letter}`
// GridPreview renders one card per history row, so its choices have to be
// keyed by the grid as well as the letter -- same shape as the container id
// `useGridPreview` already looks up.
export const previewChoiceTestId = (
  grilaId: number | string | undefined,
  letter: string,
) => `preview-choice-${grilaId ?? 'unsaved'}-${letter}`

export const homeCardTestId = (index: number) => `home-card-${index}`

export const accountTypeTestId = (index: number) => `account-type-${index}`

export const universityTestId = (index: number) => `university-${index}`

export const examTypeTestId = (index: number) => `exam-type-${index}`

// Keyed on the Stripe price id.
export const paymentCardTestId = (priceId: string) => `payment-card-${priceId}`

export const paymentCardPriceTestId = (priceId: string) =>
  `payment-card-price-${priceId}`

export const paymentCardActiveTestId = (priceId: string) =>
  `payment-card-active-${priceId}`

export const paymentCardCancellingTestId = (priceId: string) =>
  `payment-card-cancelling-${priceId}`

export const paymentCardOverdueTestId = (priceId: string) =>
  `payment-card-overdue-${priceId}`

export const paymentCardStartingTestId = (priceId: string) =>
  `payment-card-starting-${priceId}`

export const buySubscriptionTestId = (priceId: string) =>
  `buy-subscription-${priceId}`

export const cancelSubscriptionTestId = (priceId: string) =>
  `cancel-subscription-${priceId}`

export const continueSubscriptionTestId = (priceId: string) =>
  `continue-subscription-${priceId}`
